import {
    useDownloadCompletedPdf,
    useDownloadNotesPdf,
    useTrackPdfProgress,
} from "@/queries/useFetchAllNotesForCasePdf.ts";
import { Box, Button, LinearProgress, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Download } from "lucide-react";
import { enqueueSnackbar } from "notistack";
import { type ReactNode, useEffect, useState } from "react";

type Props = {
    caseUUID: string;
    disableDownload?: boolean;
};

const DownloadAllNotesButton = ({ caseUUID, disableDownload = false }: Props): ReactNode => {
    const queryClient = useQueryClient();
    const [jobId, setJobId] = useState<string | null>(null);
    const [isDownloading, setIsDownloading] = useState(false);
    const [hasDownloaded, setHasDownloaded] = useState(false); // Add flag to prevent re-downloading

    const { startDownload, isLoading: isStarting, error: startError } = useDownloadNotesPdf();
    const { progress, status, error: progressError, downloadId } = useTrackPdfProgress(jobId);
    const downloadCompletedPdf = useDownloadCompletedPdf();

    const handleDirectDownload = async () => {
        try {
            // Invalidate all pdf-progress queries to clear stale data
            await queryClient.invalidateQueries({ queryKey: ["pdf-progress"] });

            // Remove all pdf-progress queries entirely to ensure clean state
            queryClient.removeQueries({ queryKey: ["pdf-progress"] });

            // Reset all state for new download
            setJobId(null);
            setHasDownloaded(false);
            setIsDownloading(true);

            const result = await startDownload({ caseIdUUID: caseUUID });

            if (result?.jobId) {
                setJobId(result.jobId);
            }
        } catch (error) {
            console.error("Download failed:", error);
            setIsDownloading(false);
            setJobId(null);
            setHasDownloaded(false);
        }
    };

    // Handle completed download
    useEffect(() => {
        // Ensure downloadId matches the current jobId to prevent stale downloads
        const downloadIdMatchesJob = downloadId && jobId && downloadId.includes(jobId);

        if (
            status === "completed" &&
            downloadId &&
            !downloadCompletedPdf.isPending &&
            !hasDownloaded &&
            jobId &&
            downloadIdMatchesJob
        ) {
            setHasDownloaded(true);

            // Trigger the download mutation
            downloadCompletedPdf.mutate(downloadId, {
                onSuccess: ({ blob, filename }) => {
                    // Create download link and trigger download
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = url;
                    link.download = filename;
                    document.body.appendChild(link);
                    link.click();

                    // Cleanup
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(link);

                    // Reset state after a brief delay to ensure download started
                    setTimeout(() => {
                        setIsDownloading(false);
                        setJobId(null);
                    }, 1000);
                },
                onError: (error) => {
                    enqueueSnackbar(error.message, { variant: "error" });

                    // Reset state on error
                    setIsDownloading(false);
                    setJobId(null);
                    setHasDownloaded(false);
                },
            });
        }
    }, [status, downloadId, hasDownloaded, jobId, downloadCompletedPdf.isPending]);

    useEffect(() => {
        if (status === "error") {
            setIsDownloading(false);
            setJobId(null);
        }
    }, [status]);

    const getStatusText = () => {
        if (isStarting) {
            return "Starting download...";
        }

        if (downloadCompletedPdf.isPending) {
            return "Downloading file...";
        }

        switch (status) {
            case "started":
                return "Initializing...";
            case "fetching_data":
                return "Fetching notes data...";
            case "data_fetched":
                return "Processing data...";
            case "generating_pdf":
                return "Generating PDF...";
            case "preparing_download":
                return "Preparing download...";
            case "completed":
                return "Download ready!";
            case "error":
                return "Download failed";
            default:
                return "Downloading...";
        }
    };

    const currentError = startError || progressError || downloadCompletedPdf.error;
    const isActive = isStarting || isDownloading || downloadCompletedPdf.isPending;

    return (
        <Box>
            <Button
                variant="contained"
                startIcon={<Download size={16} />}
                sx={{
                    textTransform: "none",
                    fontWeight: 500,
                    backgroundColor: "black",
                    color: "white",
                }}
                disabled={disableDownload || isActive}
                onClick={handleDirectDownload}
            >
                {isActive ? getStatusText() : "Download All"}
            </Button>

            {isActive && (
                <Box sx={{ mt: 1 }}>
                    <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: "#f0f0f0",
                            "& .MuiLinearProgress-bar": {
                                backgroundColor: "#000",
                            },
                        }}
                    />
                    <Typography
                        variant="caption"
                        sx={{
                            display: "block",
                            mt: 0.5,
                            textAlign: "center",
                            color: "#666",
                        }}
                    >
                        {Math.round(progress)}%
                    </Typography>
                </Box>
            )}

            {currentError && (
                <Typography
                    variant="caption"
                    sx={{
                        color: "red",
                        display: "block",
                        mt: 1,
                        fontSize: "12px",
                    }}
                >
                    {currentError.message || "Download failed"}
                </Typography>
            )}
        </Box>
    );
};

export default DownloadAllNotesButton;
